/**
 * @author Muhammad Fahad
 * @datetime April, 13th 2022
 * @description: This is building management service that is responsible for
 *                API calling related to building management
 */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { ERROR_CODES } from '../shared/constants/error-codes';
import { catchError, map } from 'rxjs/operators';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import {
  Building,
  IBuilding,
  IBuildingExtend,
} from './building-management-interfaces';
import { StateManagementService } from '../shared/services/state-management.service';
import { Utility } from '../shared/utils/utility.service';
import {
  DuplicateDataError,
  NotFoundError,
  OperationFailedError,
  UpdateFailedError,
} from '../shared/errors/errors';
import { AddressSuggestions } from '../shared/models/data-interfaces';
import { IUser } from '../user-management/user-management-interfaces';
import { Activities } from '../shared-layout/Components/activity/activity.interface';
import { SharedDataService } from '../shared/services/shared-data.service';
import { userTypeResponse } from '../shared/constants/response-mapping';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class BuildingManagementService {
  public buildingsView = false;
  public isEmpty = false;
  private url = environment.graphqlUrl;
  // time to live (cache object) is 60 seconds
  private ttl = 60000;
  private initialTime = Date.now();
  private siteId = null;

  public triggerRerender = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private stateManagementService: StateManagementService,
    public sharedDataService: SharedDataService,
    private translate: TranslateService
  ) {}

  /**
   * @description this method set the value of triggerRerender which runs the subscription to re-render UI of building list
   * @param { boolean } value toggle boolean to re-render user interface when user perform update or delete operation building
   */
  setTriggerRerender(value: boolean) {
    this.triggerRerender.next(value);
  }

  /**
   * @description this function calls getBuildingsBySite API
   * @param {any} siteId
   * @returns { Observable<Building> }
   */
  getAllBuildingsBySiteId(
    siteId: any,
    showLoader: boolean = true,
    noErrorToasterShow: boolean = false
  ): Observable<Building> {
    if (!Utility.checkValidValue(siteId)) {
      if (window.__dev__)
        console.log(
          'getAllBuildingsBySiteId Id can not be null SiteId: ',
          siteId
        );
      return new Observable((observer) => {
        observer.error(ERROR_CODES.SITE_ID_NULL);
      });
    }
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query:
            'query GetBuildingsBySite($siteId: String!) {  getBuildingsBySite(siteId: $siteId) { buildingId buildingName buildingAddress }}',
          variables: {
            siteId: siteId,
          },
          operationName: 'GetBuildingsBySite',
        },
        {
          headers: Utility.loaderHeader(showLoader),
        }
      )
      .pipe(
        map((res) => {
          if (res?.data && res?.data?.getBuildingsBySite?.length > 0) {
            this.initialTime = Date.now();
            this.siteId = siteId;
            const buildings: Building = res.data.getBuildingsBySite;
            this.stateManagementService.updateBuildings(buildings);
            return buildings;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0].errorType);
          }
        }),
        catchError((err: any) => {
          //**On Edit Site Information we have added a flag that when clicked in button no error massage is displayed
          if (noErrorToasterShow === true) {
            return [];
          }
          switch (err.message) {
            case ERROR_CODES.BUILDING_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('NO_BUILDING_IN_THIS_SITE')
              );
            case ERROR_CODES.FAILED:
              throw new UpdateFailedError(
                this.translate.instant('REQUEST_TO_GET_BUILDINGS_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_BUILDINGS_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description this method calls update building API, parse the response and return to the caller
   * @param { string } siteId id of site on which building resides
   * @param { string } buildingId id of the building which needs to be updated
   * @param { string } buildingName name of the building which needs to be updated
   * @param { string } buildingAddress address of the building which needs to be updated
   * @param { string } buildingImg image key of the building which needs to be updated
   * @returns { Observable<IBuilding | undefined> }
   */

  updateBuilding(
    siteId: string,
    buildingId: string,
    buildingName: string,
    buildingAddress: string,
    hideLoader?: boolean
  ): Observable<IBuilding | undefined> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query:
            'mutation updateBuilding($siteId: String!, $buildingId: String!, $buildingName: String!,  $buildingAddress: String!) {  updateBuilding(siteId: $siteId, buildingId: $buildingId, buildingName: $buildingName, buildingAddress: $buildingAddress) { buildingId }}',
          variables: {
            siteId: siteId,
            buildingId: buildingId,
            buildingName: buildingName,
            buildingAddress: buildingAddress,
          },
        },
        {
          headers: Utility.loaderHeader(hideLoader),
        }
      )
      .pipe(
        map((res: any) => {
          if (res?.data && res?.data?.updateBuilding) {
            const updatedBuilding: IBuilding = {
              buildingId: res.data.updateBuilding?.buildingId,
              buildingName: buildingName,
              buildingAddress: buildingAddress,
            };
            const buildings = this.stateManagementService.getBuildings();
            buildings?.forEach((currentBuilding) => {
              if (updatedBuilding?.buildingId === currentBuilding?.buildingId) {
                currentBuilding.buildingName = updatedBuilding?.buildingName;
                currentBuilding.buildingAddress =
                  updatedBuilding?.buildingAddress;

                this.sharedDataService.updateSelectedBuilding({
                  buildingId: currentBuilding?.buildingId,
                  buildingName: currentBuilding.buildingName,
                });
              }
            });
            this.stateManagementService.updateBuildings(buildings);
            return updatedBuilding;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0].errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.BUILDING_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('NO_BUILDING_IN_THIS_SITE')
              );
            case ERROR_CODES.BUILDING_ALREADY_EXISTS:
              throw new DuplicateDataError(
                this.translate.instant('BUILDING_NAME_ALREADY_EXISTS')
              );
            case ERROR_CODES.FAILED:
              throw new UpdateFailedError(
                this.translate.instant('FAILED_TO_UPDATE_BUILDING')
              );
            default:
              throw new Error(
                this.translate.instant('FAILED_TO_UPDATE_BUILDING')
              );
          }
        })
      );
  }

  /**
   * @description this method deletes building from site by calling deleteBuilding API
   * @param { string } buildingId
   * @returns { Observable<any> }
   */
  deleteBuilding(buildingId: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation($buildingId:String!) { deleteBuilding(buildingId: $buildingId) { buildingId  }}',
        variables: {
          buildingId: buildingId,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.deleteBuilding?.buildingId) {
            const buildings = this.stateManagementService.getBuildings();
            const updatedBuildings = buildings?.filter(
              (currentBuilding) =>
                currentBuilding?.buildingId !==
                res?.data?.deleteBuilding?.buildingId
            );
            this.stateManagementService.updateBuildings(updatedBuildings);
            this.setTriggerRerender(true);
            return res?.data?.deleteBuilding;
          } else if (res.data?.deleteBuilding.hasOwnProperty('error')) {
            throw new Error(res.data?.deleteBuilding?.error[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.BUILDING_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('NO_BUILDING_IN_THIS_SITE')
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('REQUEST_TO_DELETE_BUILDING_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_DELETE_BUILDING_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description this method call addBuilding API to add a new building with site
   * @param buildingAddress
   * @param buildingImg
   * @param buildingName
   * @param buildingImgName
   * @param siteId
   * @returns {  Observable<IBuilding | undefined> }
   */
  addNewBuildingBySiteId(
    buildingAddress: string,
    buildingName: string,
    siteId: string
  ): Observable<IBuilding | undefined> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation AddBuilding($buildingAddress: String!, $buildingName: String!, $siteId: String!) {  addBuilding(buildingAddress: $buildingAddress, buildingName: $buildingName, siteId: $siteId) {  buildingId  }}',
        variables: {
          buildingAddress: buildingAddress,
          buildingName: buildingName,
          siteId: siteId,
        },
        operationName: 'AddBuilding',
      })
      .pipe(
        map((res) => {
          if (res?.data && res?.data?.addBuilding) {
            const building: IBuildingExtend = res.data.addBuilding;
            building.buildingName = buildingName;
            building.buildingAddress = buildingAddress;
            building.address = buildingAddress;

            const buildings = this.stateManagementService.getBuildings();
            buildings?.push(building);
            this.stateManagementService.updateBuildings(buildings);
            this.setTriggerRerender(true);

            return building;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.BUILDING_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('NO_BUILDING_IN_THIS_SITE')
              );
            case ERROR_CODES.BUILDING_ALREADY_EXISTS:
              throw new DuplicateDataError(
                this.translate.instant('BUILDING_NAME_ALREADY_EXISTS')
              );
            case ERROR_CODES.FAILED:
              throw new UpdateFailedError(
                this.translate.instant('BUILDINGS_LIMIT_EXCEEDED')
              );
            default:
              throw new Error(
                this.translate.instant('BUILDINGS_LIMIT_EXCEEDED')
              );
          }
        })
      );
  }

  /**
   * @description this method gets presigned url for storing image to bucket
   * @returns { Observable<any> }
   */
  getSiteBucketPresignedURL(fileType: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation PresignedURL($uploadType: String!, $fileType: String!) {  presignedURL(uploadType: $uploadType, fileType: $fileType) { fields url imageName }}',
        variables: {
          uploadType: 'building',
          fileType: fileType,
        },
        operationName: 'PresignedURL',
      })
      .pipe(
        map((res) => {
          if (res?.data?.presignedURL) {
            let result = res?.data?.presignedURL;
            if (result?.fields) result.fields = JSON.parse(result.fields);
            return result;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('FAILED_TO_GET_PRE_SIGNED_URL')
              );
            default:
              throw new Error(
                this.translate.instant('FAILED_TO_GET_PRE_SIGNED_URL')
              );
          }
        })
      );
  }

  /**
   * @description Gets address suggestions
   * @param text
   * @param hideLoader
   * @returns
   */

  getAddressSuggestions(
    text: String,
    hideLoader?: boolean
  ): Observable<AddressSuggestions> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query:
            'query ($text: String!) { getAddressSuggestions(text: $text) { suggestions } }',
          variables: {
            text: text,
          },
        },
        {
          headers: Utility.loaderHeader(hideLoader),
        }
      )
      .pipe(
        map((res: any) => {
          if (res?.data?.getAddressSuggestions) {
            return res?.data?.getAddressSuggestions?.suggestions;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('FAILED_TO_GET_ADDRESS_SUGGESTIONS')
              );
            default:
              throw new Error(
                this.translate.instant('FAILED_TO_GET_ADDRESS_SUGGESTIONS')
              );
          }
        })
      );
  }

  /**
   * @description this method uploads image to S3 bucket with the provide pre-signed url
   * @param preSignedUrl
   * @param formData
   * @returns { Observable<any> }
   */
  uploadFile(preSignedUrl: any, formData: FormData): Observable<any> {
    return this.http.post<any>(`${preSignedUrl}`, formData);
  }

  /**
   * @description this method get users by access point / unit id.
   * @param {string} accessPointId
   * @returns { Observable<IUser | undefined> }
   */
  getUserViaAccessPoint(
    accessPointId: string,
    loader?: boolean
  ): Observable<IUser[] | undefined> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `query ($accessPointId: String!) {
                getUsersByAccessPoint (accessPointId: $ accessPointId) {
                       email
                        firstName
                        lastName
                        faceId
                        invitationPending
                        isMobileCredentialEnabled
                        mFAEnabled
                        phoneNumber
                        userId
                        userImgKey
                        userImgName
                        userImgUrl
                        userType
                        isDeactivated
        }
            }
        `,
          variables: {
            accessPointId,
          },
        },
        {
          headers: Utility.loaderHeader(loader),
        }
      )
      .pipe(
        map((res: any) => {
          if (res?.data?.getUsersByAccessPoint) {
            const usersList: IUser[] = res.data.getUsersByAccessPoint.map(
              (user: IUser) => {
                if (user && userTypeResponse.hasOwnProperty(user.userType)) {
                  return {
                    ...user,
                    userType: this.translate.instant(
                      userTypeResponse[user.userType!]
                    ),
                  };
                }
                return user;
              }
            );
            this.stateManagementService.updateUnitOrAccessPointUsersList(
              usersList
            );
            return usersList;
          } else if (res?.errors?.length > 0) {
            this.stateManagementService.updateUnitOrAccessPointUsersList([]);
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          this.stateManagementService.updateUnitOrAccessPointUsersList([]);
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new NotFoundError(
                this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description this method get users by access point / unit id.
   * @param {string} unitId
   * @returns { Observable<IUser | undefined> }
   */
  getUserViaUnit(
    unitId: string,
    storeUpdate: boolean = true,
    loader?: boolean
  ): Observable<IUser[] | undefined> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `query ($unitId: String!) {
                  getUsersByUnit (unitId: $ unitId) {
                         email
                          firstName
                          lastName
                          faceId
                          invitationPending
                          isMobileCredentialEnabled
                          mFAEnabled
                          phoneNumber
                          userId
                          userImgKey
                          userImgName
                          userImgUrl
                          userType
                          isDeactivated
          }
              }
          `,
          variables: {
            unitId,
          },
        },
        {
          headers: Utility.loaderHeader(loader),
        }
      )
      .pipe(
        map((res: any) => {
          if (res?.data?.getUsersByUnit) {
            const usersList: IUser[] = res?.data?.getUsersByUnit;

            const updatedUsersList: IUser[] = usersList.map((user: IUser) => {
              const userType = user.userType;
              if (userType !== undefined && userTypeResponse[userType]) {
                return {
                  ...user,
                  userType: this.translate.instant(userTypeResponse[userType]),
                };
              }
              return user; // Keep the user object unchanged if userType doesn't exist or is invalid
            });

            if (storeUpdate) {
              this.stateManagementService.updateUnitOrAccessPointUsersList(
                updatedUsersList
              );
            }
            return updatedUsersList;
          } else if (res?.errors?.length > 0) {
            this.stateManagementService.updateUnitOrAccessPointUsersList([]);
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          this.stateManagementService.updateUnitOrAccessPointUsersList([]);
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new NotFoundError(
                this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description this method calls API to get all this Devices by accessPointId Id list
   * @param { string } buildingId
   * @param {boolean}  prepend will be false and replace object 
   * @param { filterCriteria } { eventType: []; dateTo: string; dateFrom: string; searchEntity: searchEntity | string;
  pagination: Pagination;}

   */
  getEventHistoryByBuilding(
    buildingId: string,
    filterCriteria: any,
    prepend = false,
    loader?: boolean
  ): Observable<any | undefined> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `query($buildingId: String!, $filterCriteria: FilterCriteria!) { getEventHistoryByBuilding (buildingId: $buildingId, filterCriteria: $filterCriteria) {events {id, eventDescription, eventCode, user, timestamp, isoTime, deviceName, unitName, buildingName, accessPointName, eventTitle, extraDescription, source, sourceName, isIssue, isAbnormal, isMaster, floorName, wingName}, totalRecords, cursor} }`,
          variables: {
            buildingId,
            filterCriteria,
          },
        },
        {
          headers: Utility.loaderHeader(loader),
        }
      )
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.getEventHistoryByBuilding) {
              let activityList: Activities =
                res?.data?.getEventHistoryByBuilding;
              if (activityList) {
                activityList.events =
                  res?.data?.getEventHistoryByBuilding?.events?.map(
                    (event: any) => {
                      event.eventType = event.eventDescription;
                      return event;
                    }
                  );
              }
              if (prepend) {
                this.stateManagementService.prependBuildingAcvities(
                  activityList
                );
              } else {
                this.stateManagementService.updateBuildingAcvities(
                  activityList
                );
              }
              return activityList;
            } else if (res?.errors?.length > 0) {
              this.stateManagementService.updateBuildingAcvities(
                {} as Activities
              );
              let error = res?.errors[0];
              switch (error.errorType) {
                case ERROR_CODES.INTERNAL_SERVER_ERROR:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_PARTNER_DEVICES')
                  );
                default:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_PARTNER_DEVICES')
                  );
              }
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }
}
